import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { Divider } from '@mui/material';
import ArrowBackIosNewTwoToneIcon from '@mui/icons-material/ArrowBackIosNewTwoTone';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
// Import Icon สำหรับ PDF
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import RejectedButton from '../../components/Button/RejectedButton';
import addCommas from '../../utils/functions/commas';
import TablePendingDetail from '../../components/TablePendingDetail/TablePendingDetail';
import ApprovedButton from '../../components/Button/ApprovedButton';
import { usePendingDetailQuery } from '../../services/evidencePay';
import jsPDF from 'jspdf';
import CircularProgress from '@mui/material/CircularProgress';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useTranslation } from 'react-i18next';

function PendingDetail() {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [dataPendingDetail, setDataPendingDetail] = useState([]);

  const [invoice, setInvoice] = useState([]);
  const [transaction, setTransaction] = useState([]);
  const [sorgcodeForSuperAdmin, setSorgcodeForSuperAdmin] = useState([]);
  const location = useLocation();
  const [sendVelue, setSendVelue] = useState([]);
  const [payAmount, setPayAmount] = useState(0);
  const navigate = useNavigate();
  
  const goBack = () => {
    navigate(-1);
  };

  // --- Helper: เช็คว่าเป็น PDF หรือไม่ ---
  const getFileType = (base64) => {
    // เช็ค signature ของ PDF (Base64 ของ PDF มักจะขึ้นต้นด้วย JVBERi0)
    if (base64 && base64.startsWith('JVBERi0')) {
      return 'pdf';
    }
    return 'image';
  };

  // --- Logic 1: เปิดไฟล์ PDF โดยตรง ---
  const openPdfDirectly = (base64) => {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, '_blank');
    } catch (error) {
      console.error('Error opening PDF:', error);
    }
  };

  // --- Logic 2: แปลงรูปภาพเป็น PDF (Code เดิมของคุณ) ---
  const convertImageToPdf = (imgBase64) => {
    const img = new Image();
    img.src = `data:image/jpeg;base64,${imgBase64}`;

    img.onload = function () {
      const imgWidth = this.width;
      const imgHeight = this.height;
      const pdf = new jsPDF({
        orientation: imgWidth > imgHeight ? 'l' : 'p',
        unit: 'pt',
        format: [imgWidth, imgHeight],
      });
      pdf.addImage(this, 'JPEG', 0, 0, imgWidth, imgHeight);
      const pdfUrl = pdf.output('bloburl');
      window.open(pdfUrl, '_blank');
    };

    img.onerror = function () {
      console.error('ไม่สามารถโหลดรูปภาพสำหรับ PDF ได้');
    };
  };

  // --- Main Handler: ตัดสินใจว่าจะเปิดแบบไหน ---
  const handlePreview = (base64) => {
    const type = getFileType(base64);
    if (type === 'pdf') {
      openPdfDirectly(base64);
    } else {
      convertImageToPdf(base64);
    }
  };

  const { data: pendingDetail, isLoading } = usePendingDetailQuery(
    {
      transactionId: location.state?._id,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (pendingDetail?.data?.invoice?.length > 0) {
      const totalPayAmount = pendingDetail.data.invoice.reduce(
        (sum, item) => sum + Number(item.pay_amount || 0),
        0,
      );
      setPayAmount(totalPayAmount);
    }
  }, [pendingDetail]);

  useEffect(() => {
    setSendVelue(pendingDetail?.data.transaction[0]);
    setInvoice(pendingDetail?.data.invoice);
    setDataPendingDetail(pendingDetail?.data.transaction[0]);
    setTransaction(pendingDetail?.data.transaction[0]);
  }, [pendingDetail]);

  useEffect(() => {
    setSorgcodeForSuperAdmin(dataPendingDetail?.sorgcode);
  }, [dataPendingDetail]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  const customerCodes = [
    ...(Array.isArray(dataPendingDetail?.customer_codes)
      ? dataPendingDetail.customer_codes
      : []),
    ...(Array.isArray(invoice)
      ? invoice.map(
          (item) =>
            item?.customer_code ||
            item?.sold_to_party ||
            item?.customer?.customer_code,
        )
      : []),
  ].filter(Boolean);
  const uniqueCustomerCodes = [...new Set(customerCodes)];
  const customerIdDisplay =
    uniqueCustomerCodes.length > 0
      ? uniqueCustomerCodes.join(', ')
      : dataPendingDetail?.customer_code;

  // --- Helper สำหรับ Render รูป หรือ ตัวอย่าง PDF ---
  const renderFilePreview = (file) => {
    const type = getFileType(file.imgBase64);
    
    // ถ้าเป็น PDF
    if (type === 'pdf') {
      return (
        <div 
          className="relative border rounded shadow-md bg-gray-50 overflow-hidden group hover:scale-105 transition-transform duration-300"
          style={{ width: '300px', height: '300px' }} 
        >
          {/* 1. ใช้ object tag เพื่อแสดง Preview PDF 
            ใช้ pointer-events-none เพื่อไม่ให้เมาส์ไป interact กับ PDF โดยตรง (เช่น การเลือก text หรือ scroll ในกล่องเล็ก)
            เพื่อให้คลิกแล้วเด้งไปที่ handlePreview ของ div แม่แทน
          */}
          <object
            data={`data:application/pdf;base64,${file.imgBase64}`}
            type="application/pdf"
            width="100%"
            height="100%"
            className="w-full h-full pointer-events-none object-contain"
          >
             {/* กรณี Browser ไม่รองรับการ Preview ใน object */}
             <div className="flex flex-col items-center justify-center h-full">
                 <PictureAsPdfIcon sx={{ fontSize: 80, color: '#d32f2f' }} />
             </div>
          </object>

          {/* 2. Layer ใสสำหรับดัก Event Click เพื่อเปิดเต็มจอ */}
          <div 
             className="absolute inset-0 cursor-pointer hover:bg-black/5 transition-colors z-10"
             onClick={() => handlePreview(file.imgBase64)}
          />

          {/* 3. ป้ายกำกับว่าเป็น PDF มุมขวาล่าง */}
          <div className="absolute bottom-2 right-2 bg-white/90 px-2 py-1 rounded shadow-sm border flex items-center gap-1 z-20 pointer-events-none">
             <PictureAsPdfIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
             <span className="text-xs font-bold text-gray-700">PDF</span>
          </div>
        </div>
      );
    } else {
      // ถ้าเป็นรูปภาพปกติ
      return (
        <div className="relative group">
            <img
            className="rounded shadow-md cursor-pointer hover:scale-105 transition-transform mx-auto object-cover"
            width="300"
            height="300"
            style={{ width: '300px', height: '300px', objectFit: 'contain', backgroundColor: '#f9f9f9' }}
            src={`data:image/jpeg;base64,${file.imgBase64}`}
            alt="Evidence"
            onClick={() => handlePreview(file.imgBase64)}
            />
        </div>
      );
    }
  };

  return (
    <div className=" flex flex-col p-2 border-0 md:border-2 border-gray-200 rounded-md bg-white w-full h-auto">
      <div>
        <Button onClick={goBack} sx={{ color: 'black' }}>
          <ArrowBackIosNewTwoToneIcon />
        </Button>
      </div>

      <div className="text-center font-bold text-2xl mb-6">
        {t('app.proofOfPaymentDetails')}
      </div>

      {/* Payment evidence section */}
      <div className="bg-blue-50 p-5 rounded-lg mb-6 border border-blue-200">
        <div className="text-lg font-semibold text-blue-700 mb-4">
          {t('app.paymentProof')}
        </div>

        {pendingDetail?.data.paymentFile?.length >= 3 ? (
          <Swiper
            modules={[Navigation]}
            spaceBetween={20}
            slidesPerView={2}
            navigation
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {pendingDetail.data.paymentFile.map((file, index) => (
              <SwiperSlide key={index}>
                <div
                  className="text-center shadow-md p-2 rounded min-w-[200px] flex justify-center items-center bg-white"
                  style={{ height: '320px' }}
                >
                  <Tooltip title={t('กดเพื่อดูเอกสาร')} placement="right">
                    {renderFilePreview(file)}
                  </Tooltip>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="flex flex-wrap justify-center gap-6">
            {pendingDetail?.data.paymentFile?.map((file, index) => (
              <div key={index} className="text-center">
                 <Tooltip title={t('กดเพื่อดูเอกสาร')} placement="right">
                    {renderFilePreview(file)}
                 </Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* WHT evidence section */}
      {pendingDetail?.data.whtFile && pendingDetail.data.whtFile.length > 0 && (
        <div className="bg-gray-100 p-5 rounded-lg mb-6 border border-gray-200">
          <div className="text-lg font-semibold text-yellow-700 mb-4">
            {t('app.whtDocument')}
          </div>

          {pendingDetail.data.whtFile.length >= 3 ? (
            <Swiper
              modules={[Navigation]}
              spaceBetween={20}
              slidesPerView={2}
              navigation
              breakpoints={{
                0: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
            >
              {pendingDetail.data.whtFile.map((file, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="text-center shadow-md p-2 rounded min-w-[200px] flex justify-center items-center bg-white"
                    style={{ height: '320px' }}
                  >
                    <Tooltip title={t('กดเพื่อดูเอกสาร')} placement="right">
                      {renderFilePreview(file)}
                    </Tooltip>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="flex flex-wrap justify-center gap-6">
              {pendingDetail.data.whtFile.map((file, index) => (
                <div key={index} className="text-center">
                  <Tooltip title={t('กดเพื่อดูเอกสาร')} placement="right">
                    {renderFilePreview(file)}
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dialog สำหรับดูภาพ (กรณีอยาก Preview รูปใน Modal - Optional) */}
      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>รูปภาพ</DialogTitle>
        <DialogContent>
          {selectedImage && getFileType(selectedImage) !== 'pdf' ? (
             <img
                width="500px"
                height="500px"
                src={`data:image/jpeg;base64,${selectedImage}`}
                alt="Selected"
                style={{ objectFit: 'contain' }}
              />
          ) : (
             <div className="flex flex-col items-center p-10">
                 <PictureAsPdfIcon sx={{ fontSize: 100, color: '#d32f2f' }} />
                 <p>Document is PDF</p>
             </div>
          )}
         
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>

      {/* ข้อมูลหลัก */}
      <div className="md:grid grid-cols-3  p-5 space-y-3   mt-6 bg-white/80 backdrop-blur-md shadow-xl rounded-xl  text-gray-800 border border-gray-200">
        {[
          [
            t('app.paymentDate'),
            dayjs(dataPendingDetail?.payment_date).format(
              'YYYY-MM-DD • HH:mm:ss',
            ),
          ],
          [t('app.customerID'), customerIdDisplay],
          [
            t('app.paymentAmount'),
            addCommas(
              dataPendingDetail?.transfer_amount || dataPendingDetail?.total_pay_amount,
            ),
          ],
          [t('app.customerName'), dataPendingDetail?.customer_name],
          [
            t('app.lateFeeThb'),
            dataPendingDetail?.fines
              ? addCommas(dataPendingDetail.fines)
              : '0.00',
          ],
          [t('app.bankAccountNo'), dataPendingDetail?.bank_no],
          [
            t('app.fee'),
            dataPendingDetail?.total_fee
              ? addCommas(dataPendingDetail.total_fee)
              : '0.00',
          ],
          [t('app.email'), dataPendingDetail?.payer_email],
          ['Invoice Grand Total', addCommas(payAmount)],
        ].map(([label, value]) => (
          <div key={label} className="flex flex-col">
            <div className="text-gray-500 text-sm tracking-wide font-semibold mb-1">
              {label}
            </div>
            <div className="text-lg font-medium text-gray-900">
              {value || '-'}
            </div>
          </div>
        ))}
      </div>

      <TablePendingDetail
        invoice={invoice}
        transaction={transaction}
        uploadRef={location.state?.uploadRef}
      />

      <ApprovedButton
        t={t}
        uploadRef={location.state?.uploadRef}
        sendVelue={sendVelue}
        sorgcodeForSuperAdmin={sorgcodeForSuperAdmin}
      />
      <Divider sx={{ marginTop: '3%' }} />
      <RejectedButton
        t={t}
        sendVelue={sendVelue}
        uploadRef={location.state?.uploadRef}
      />
    </div>
  );
}

export default PendingDetail;
